import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';

import { User } from 'store/types';
import { environment } from 'environments';
import { HttpContentType } from 'enums';
import { ApiCompensation, Compensation } from 'types';
import { REVERSE_DATE_FORMAT } from 'constants-module';

const USERS_CONTROLLER_BASE_URL = `${environment.baseApiUrl}Users`;

export const fetchCurrentUser = createAsyncThunk('get:Users/current', async () => {
  const { data } = await axios.get<User>(`${USERS_CONTROLLER_BASE_URL}/current`);

  return data;
});

export const fetchCurrentYearUserCompensations = createAsyncThunk('get:Users/requests/currentYear', async () => {
  const { data } = await axios.get<Compensation[]>(`${USERS_CONTROLLER_BASE_URL}/requests?currentYear=true`);

  return data;
});

export const fetchArchivedUserCompensations = createAsyncThunk('get:Users/requests/archived', async () => {
  const { data } = await axios.get<Compensation[]>(`${USERS_CONTROLLER_BASE_URL}/requests?currentYear=false`);

  return data;
});

export async function getUserCompensationById(requestId: string): Promise<Compensation> {
  const { data } = await axios.get<Compensation>(`${USERS_CONTROLLER_BASE_URL}/requests/${requestId}`);

  return data;
}

export const submitCompensationAttachments = createAsyncThunk(
  'post:User/requests/compensationRequestID/attachments',
  async ({ compensationRequestID, files }: { compensationRequestID: number; files: File[] }) => {
    const formData = new FormData();

    files.forEach((file: Blob) => formData.append('attachments', file));

    const { data } = await axios.post(
      `${USERS_CONTROLLER_BASE_URL}/requests/${compensationRequestID}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': HttpContentType.MultipartFormData,
        },
      },
    );

    return data;
  },
);

export const updateUserCompensation = createAsyncThunk(
  'put:User/requests/{requestId}',
  async (compensation: Compensation) => {
    const { data } = await axios.put(`${USERS_CONTROLLER_BASE_URL}/requests/${compensation.compensationRequestID}`, {
      type: compensation.type,
      amount: compensation.amount,
      currency: compensation.currency,
      targetPeriod: dayjs(compensation.targetPeriod).startOf('month').startOf('day').format(REVERSE_DATE_FORMAT),
      billedOn: compensation.billedOn && dayjs(compensation.billedOn).startOf('day').format(REVERSE_DATE_FORMAT),
      employeeComment: compensation.employeeComment,
    });

    return data;
  },
);

export const deleteCompensationAttachment = createAsyncThunk(
  'delete:User/requests/{requestID}/attachments',
  async ({ compensationRequestID, attachmentID }: { compensationRequestID: number; attachmentID: string }) => {
    const { data } = await axios.delete(
      `${USERS_CONTROLLER_BASE_URL}/requests/${compensationRequestID}/attachments?attachment=${attachmentID}`,
    );

    return data;
  },
);

export const createCompensation = createAsyncThunk('post:Users/requests', async (apiCompensation: ApiCompensation) => {
  const { data } = await axios.post<Compensation>(`${USERS_CONTROLLER_BASE_URL}/requests`, apiCompensation);

  return data;
});
